import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { apiSend } from '../lib/api'
import type { Device, DeviceCheck } from '../lib/types'

/** Höchstens so viele Prüfungen je Gerät; die Zentrale prüft dasselbe. */
export const MAX_PRUEFUNGEN = 10

const ARTEN: { type: DeviceCheck['type']; label: string; beispiel: string; hilfe: string }[] = [
  {
    type: 'ping',
    label: 'Ping',
    beispiel: '1.1.1.1',
    hilfe: 'Adresse oder Hostname; gemessen werden Antwortzeit und Verlust.',
  },
  {
    type: 'dns',
    label: 'DNS',
    beispiel: 'www.example.at',
    hilfe: 'Name, der aufgelöst werden muss; mit name@server wird ein bestimmter DNS-Server gefragt.',
  },
  {
    type: 'http',
    label: 'Webseite',
    beispiel: 'https://portal.example.at',
    hilfe: 'http- oder https-Adresse; bei https wird auch das Zertifikat überwacht.',
  },
]

function artLabel(type: DeviceCheck['type']): string {
  return ARTEN.find((a) => a.type === type)?.label ?? type
}

/**
 * Prüfungen einer Internetleitung: welche Ziele der Agent aus dem Kundennetz
 * anpingt, welche Namen er auflöst und welche Seiten er abruft. Jede Änderung
 * wird sofort gespeichert; der Agent bekommt sie mit der nächsten
 * Konfiguration.
 */
export function PruefungenForm({ device, onSaved }: { device: Device; onSaved: () => void }) {
  const checks = device.checks ?? []
  const [type, setType] = useState<DeviceCheck['type']>('ping')
  const [target, setTarget] = useState('')
  const art = ARTEN.find((a) => a.type === type)!

  const save = useMutation({
    mutationFn: (next: DeviceCheck[]) => apiSend<Device>('PATCH', `/devices/${device.id}`, { checks: next }),
    onSuccess: () => {
      setTarget('')
      onSaved()
    },
  })

  const ziel = target.trim()
  const doppelt = checks.some((c) => c.type === type && c.target === ziel)

  return (
    <div className="stack" style={{ gap: 10 }}>
      <h3 className="card-title" style={{ fontSize: 14 }}>
        Prüfungen
      </h3>
      {checks.length === 0 ? (
        <span className="muted" style={{ fontSize: 13 }}>
          Noch keine Prüfung. Gemessen wird bis dahin nur der Ping auf {device.mgmt_ip}.
        </span>
      ) : (
        <ul className="stack" style={{ gap: 6, listStyle: 'none', margin: 0, padding: 0 }} aria-label="Prüfungen">
          {checks.map((c) => (
            <li key={`${c.type}|${c.target}`} className="row" style={{ gap: 10 }}>
              <span className="badge badge-info">{artLabel(c.type)}</span>
              <span className="mono">{c.target}</span>
              <button
                type="button"
                className="btn btn-sm"
                disabled={save.isPending}
                aria-label={`${artLabel(c.type)} ${c.target} entfernen`}
                onClick={() => save.mutate(checks.filter((x) => x !== c))}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="row"
        style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}
        onSubmit={(e) => {
          e.preventDefault()
          if (ziel && !doppelt && checks.length < MAX_PRUEFUNGEN) save.mutate([...checks, { type, target: ziel }])
        }}
      >
        <div className="field" style={{ minWidth: 140 }}>
          <label htmlFor="pruefung-art">Art</label>
          <select
            id="pruefung-art"
            className="input"
            value={type}
            onChange={(e) => setType(e.target.value as DeviceCheck['type'])}
          >
            {ARTEN.map((a) => (
              <option key={a.type} value={a.type}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 260, flex: 1 }}>
          <label htmlFor="pruefung-ziel">Ziel</label>
          <input
            id="pruefung-ziel"
            className="input mono"
            value={target}
            placeholder={art.beispiel}
            onChange={(e) => setTarget(e.target.value)}
          />
        </div>
        <button
          type="submit"
          className="btn"
          disabled={save.isPending || !ziel || doppelt || checks.length >= MAX_PRUEFUNGEN}
        >
          Prüfung hinzufügen
        </button>
      </form>
      <span className="muted" style={{ fontSize: 12 }}>
        {checks.length >= MAX_PRUEFUNGEN ? `Höchstens ${MAX_PRUEFUNGEN} Prüfungen je Gerät.` : art.hilfe}
      </span>
      {doppelt ? <span style={{ color: 'var(--warn)', fontSize: 13 }}>Diese Prüfung gibt es schon.</span> : null}
      {save.isError ? <span style={{ color: 'var(--crit)' }}>{(save.error as Error).message}</span> : null}
    </div>
  )
}
